import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AllowlistEntryDto,
  AppLogEntry,
  BackupDto,
  BackupMode,
  BackupScheduleDto,
  BackupScheduleInput,
  ConnectionDto,
  ConnectionProbeResult,
  DbInsightsBackendActionResponseDto,
  DbInsightsExplainResponseDto,
  DbInsightsIndexesResponseDto,
  DbInsightsSnapshotDto,
  ExecuteConfirmation,
  ExecuteResponse,
  OperationId,
  OperationSummary,
  PlanResponse,
  PreflightResponse,
  ProviderCollectionsProvidersDto,
  ProviderCollectionsSampleDto,
  ProviderCollectionsSummaryDto,
  RunDetailDto,
  RunSummaryDto,
  SessionUserDto,
  SqlLibraryItemDto,
  TargetBrandDto,
  TargetBusinessUnitDto,
  TargetCategoryDto,
  TargetOrganizationDto,
  TargetRetailerDto,
  TargetUserDto,
  ToolkitUserDto,
  CreateToolkitUserRequest,
  UpdateToolkitUserRequest,
} from '@visualitics-toolkit/shared';

import { api } from './api-client';

export const queryKeys = {
  session: ['session'] as const,
  connections: ['connections'] as const,
  toolkitUsers: ['toolkit-users'] as const,
  operations: (connectionId?: string) => ['operations', connectionId ?? 'active'] as const,
  allowlist: (connectionId: string) => ['allowlist', connectionId] as const,
  runs: (filter: Record<string, string | undefined>) => ['runs', filter] as const,
  run: (id: string) => ['run', id] as const,
  backups: (connectionId?: string) => ['backups', connectionId ?? 'all'] as const,
  backupSchedules: ['backup-schedules'] as const,
  sqlLibrary: (search?: string) => ['sql-library', search ?? ''] as const,
  dbInsights: (connectionId?: string) => ['db-insights', connectionId ?? 'active'] as const,
  providerCollectionsProviders: (connectionId: string | undefined, organizationId: string) =>
    ['provider-collections', 'providers', connectionId ?? 'active', organizationId] as const,
  providerCollectionsSummary: (
    connectionId: string | undefined,
    filter: Record<string, string | undefined>,
  ) => ['provider-collections', 'summary', connectionId ?? 'active', filter] as const,
  providerCollectionsSample: (
    connectionId: string | undefined,
    filter: Record<string, string | undefined>,
  ) => ['provider-collections', 'sample', connectionId ?? 'active', filter] as const,
  logDays: ['log-days'] as const,
  logs: (filter: Record<string, string | undefined>) => ['logs', filter] as const,
  organizations: (search: string) => ['target', 'organizations', search] as const,
  brands: (organizationId: string) => ['target', 'brands', organizationId] as const,
  categories: (organizationId: string) => ['target', 'categories', organizationId] as const,
  businessUnits: (organizationId: string) => ['target', 'business-units', organizationId] as const,
  retailers: (organizationId: string) => ['target', 'retailers', organizationId] as const,
  users: (search: string) => ['target', 'users', search] as const,
};

function invalidateTargetLookups(client: QueryClient): void {
  void client.invalidateQueries({ queryKey: ['target'] });
}

// ─── Sessão e conexões ──────────────────────────────────────────────────────

export function useSession(): UseQueryResult<SessionUserDto | null> {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: () => api.get<SessionUserDto | null>('/auth/session'),
    retry: false,
    staleTime: 30_000,
  });
}

export function useConnections(): UseQueryResult<ConnectionDto[]> {
  return useQuery({ queryKey: queryKeys.connections, queryFn: () => api.get<ConnectionDto[]>('/connections') });
}

/** Conexão ativa do operador; define a cor do shell e o escopo de toda operação. */
export function useActiveConnection(): ConnectionDto | null {
  const session = useSession();
  const connections = useConnections();

  if (!connections.data?.length) return null;
  const activeId = session.data?.activeConnectionId;
  return (
    connections.data.find((connection) => connection.id === activeId) ??
    connections.data.find((connection) => connection.isDefault) ??
    connections.data[0]
  );
}

export function useActivateConnection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) => api.post<ConnectionDto>(`/connections/${connectionId}/activate`),
    onSuccess: () => {
      void client.invalidateQueries();
    },
  });
}

export function useTestConnection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) =>
      api.post<ConnectionProbeResult>(`/connections/${connectionId}/test`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.connections });
    },
  });
}

export type ConnectionProbeInput = {
  host: string;
  port: number;
  database: string;
  username: string;
  password?: string;
  sslmode: string;
  connectionId?: string;
};

/** Testa os campos do formulário sem gravar lastTestOk. */
export function useProbeConnection() {
  return useMutation({
    mutationFn: (values: ConnectionProbeInput) =>
      api.post<ConnectionProbeResult>('/connections/probe', values),
  });
}

export function useSaveConnection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Record<string, unknown> }) =>
      id ? api.put<ConnectionDto>(`/connections/${id}`, values) : api.post<ConnectionDto>('/connections', values),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.connections });
    },
  });
}

export function useDeleteConnection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/connections/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.connections });
    },
  });
}

// ─── Catálogo e allowlist ───────────────────────────────────────────────────

export function useOperations(connectionId?: string): UseQueryResult<OperationSummary[]> {
  return useQuery({
    queryKey: queryKeys.operations(connectionId),
    queryFn: () => api.get<OperationSummary[]>('/operations', { connectionId }),
    enabled: Boolean(connectionId),
  });
}

export function useAllowlist(connectionId?: string): UseQueryResult<AllowlistEntryDto[]> {
  return useQuery({
    queryKey: queryKeys.allowlist(connectionId ?? ''),
    queryFn: () => api.get<AllowlistEntryDto[]>(`/connections/${connectionId}/allowlist`),
    enabled: Boolean(connectionId),
  });
}

export function useSaveAllowlist(connectionId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (overrides: { operationId: string; allowed: boolean | null }[]) =>
      api.put<AllowlistEntryDto[]>(`/connections/${connectionId}/allowlist`, { overrides }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.allowlist(connectionId) });
      void client.invalidateQueries({ queryKey: ['operations'] });
    },
  });
}

// ─── Operadores do toolkit (Admin) ──────────────────────────────────────────

export function useToolkitUsers(enabled = true): UseQueryResult<ToolkitUserDto[]> {
  return useQuery({
    queryKey: queryKeys.toolkitUsers,
    queryFn: () => api.get<ToolkitUserDto[]>('/users'),
    enabled,
  });
}

export function useCreateToolkitUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateToolkitUserRequest) => api.post<ToolkitUserDto>('/users', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.toolkitUsers });
    },
  });
}

export function useUpdateToolkitUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateToolkitUserRequest }) =>
      api.patch<ToolkitUserDto>(`/users/${id}`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.toolkitUsers });
      void client.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
}

export function useResetToolkitUserPassword() {
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api.post<{ ok: true }>(`/users/${id}/password`, { password }),
  });
}

// ─── Ciclo de vida da operação ──────────────────────────────────────────────

export function usePreflight(operationId: OperationId) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (params: Record<string, unknown>) =>
      api.post<PreflightResponse>(`/operations/${operationId}/preflight`, { params }),
    onSuccess: () => invalidateTargetLookups(client),
  });
}

export function usePlan(operationId: OperationId) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (params: Record<string, unknown>) =>
      api.post<PlanResponse>(`/operations/${operationId}/plan`, { params }),
    onSuccess: () => invalidateTargetLookups(client),
  });
}

export function useExecute(operationId: OperationId) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { params: Record<string, unknown>; confirmation: ExecuteConfirmation }) =>
      api.post<ExecuteResponse>(`/operations/${operationId}/execute`, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['runs'] });
      invalidateTargetLookups(client);
    },
  });
}

// ─── Runs ───────────────────────────────────────────────────────────────────

export function useRuns(filter: { connectionId?: string; operationId?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: queryKeys.runs({
      connectionId: filter.connectionId,
      operationId: filter.operationId,
      limit: filter.limit ? String(filter.limit) : undefined,
    }),
    queryFn: () => api.get<RunSummaryDto[]>('/runs', filter),
  });
}

export function useRun(id: string): UseQueryResult<RunDetailDto> {
  return useQuery({ queryKey: queryKeys.run(id), queryFn: () => api.get<RunDetailDto>(`/runs/${id}`) });
}

// ─── Backups ────────────────────────────────────────────────────────────────

export function useBackups(connectionId?: string): UseQueryResult<BackupDto[]> {
  return useQuery({
    queryKey: queryKeys.backups(connectionId),
    queryFn: () => api.get<BackupDto[]>('/backups', { connectionId }),
  });
}

export function useStartBackup() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      connectionId?: string;
      mode: BackupMode;
      organizationId?: string;
      note?: string;
    }) => api.post<BackupDto>('/backups', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['backups'] });
    },
  });
}

export function useRestoreBackup() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      backupId: string;
      connectionId?: string;
      confirmation: ExecuteConfirmation;
      dropExisting?: boolean;
    }) =>
      api.post<{ messages: string[]; backupId: string }>(`/backups/${input.backupId}/restore`, {
        connectionId: input.connectionId,
        confirmation: input.confirmation,
        dropExisting: input.dropExisting,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['backups'] });
      void client.invalidateQueries({ queryKey: queryKeys.connections });
    },
  });
}

// ─── Agendamentos de backup ─────────────────────────────────────────────────

export function useBackupSchedules(enabled = true): UseQueryResult<BackupScheduleDto[]> {
  return useQuery({
    queryKey: queryKeys.backupSchedules,
    queryFn: () => api.get<BackupScheduleDto[]>('/backup-schedules'),
    enabled,
  });
}

export function useSaveBackupSchedule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id?: string; values: BackupScheduleInput }) =>
      id
        ? api.put<BackupScheduleDto>(`/backup-schedules/${id}`, values)
        : api.post<BackupScheduleDto>('/backup-schedules', values),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.backupSchedules });
    },
  });
}

export function useDeleteBackupSchedule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/backup-schedules/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.backupSchedules });
    },
  });
}

export function useRunBackupSchedule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<BackupDto>(`/backup-schedules/${id}/run`, {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.backupSchedules });
      void client.invalidateQueries({ queryKey: ['backups'] });
    },
  });
}

// ─── Biblioteca SQL ─────────────────────────────────────────────────────────

export function useSqlLibrary(search?: string): UseQueryResult<SqlLibraryItemDto[]> {
  return useQuery({
    queryKey: queryKeys.sqlLibrary(search),
    queryFn: () => api.get<SqlLibraryItemDto[]>('/sql-library', { search }),
  });
}

export function useSaveSqlLibraryItem() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Record<string, unknown> }) =>
      id
        ? api.put<SqlLibraryItemDto>(`/sql-library/${id}`, values)
        : api.post<SqlLibraryItemDto>('/sql-library', values),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['sql-library'] });
    },
  });
}

export function useDeleteSqlLibraryItem() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/sql-library/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['sql-library'] });
    },
  });
}

// ─── Logs ───────────────────────────────────────────────────────────────────

export function useLogDays(): UseQueryResult<{ dateKey: string; filename: string }[]> {
  return useQuery({
    queryKey: queryKeys.logDays,
    queryFn: () => api.get<{ dateKey: string; filename: string }[]>('/logs/days'),
  });
}

export function useLogs(filter: { date?: string; level?: string; search?: string }) {
  return useQuery({
    queryKey: queryKeys.logs(filter),
    queryFn: () => api.get<AppLogEntry[]>('/logs', { ...filter, limit: 2000 }),
    enabled: Boolean(filter.date),
    placeholderData: (previous) => previous,
  });
}

export function useDbInsightsSnapshot(connectionId?: string) {
  return useQuery({
    queryKey: queryKeys.dbInsights(connectionId),
    queryFn: () => api.get<DbInsightsSnapshotDto>('/db-insights/snapshot', { connectionId }),
    refetchInterval: 10_000,
  });
}

export function useDbInsightsExplain(connectionId?: string) {
  return useMutation({
    mutationFn: (input: { sql: string; analyze?: boolean }) =>
      api.post<DbInsightsExplainResponseDto>('/db-insights/explain', {
        connectionId,
        sql: input.sql,
        analyze: input.analyze,
      }),
  });
}

export function useDbInsightsIndexes(connectionId?: string) {
  return useMutation({
    mutationFn: (input: { table: string; schema?: string }) =>
      api.get<DbInsightsIndexesResponseDto>('/db-insights/indexes', {
        connectionId,
        table: input.table,
        schema: input.schema,
      }),
  });
}

export function useDbInsightsCancelBackend(connectionId?: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { pid: number; confirmToken: string }) =>
      api.post<DbInsightsBackendActionResponseDto>('/db-insights/backends/cancel', {
        connectionId,
        ...input,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.dbInsights(connectionId) });
    },
  });
}

export function useDbInsightsTerminateBackend(connectionId?: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { pid: number; confirmToken: string }) =>
      api.post<DbInsightsBackendActionResponseDto>('/db-insights/backends/terminate', {
        connectionId,
        ...input,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.dbInsights(connectionId) });
    },
  });
}

export function useProviderCollectionsProviders(
  organizationId: string | undefined,
  connectionId?: string,
) {
  return useQuery({
    queryKey: queryKeys.providerCollectionsProviders(connectionId, organizationId ?? ''),
    queryFn: () =>
      api.get<ProviderCollectionsProvidersDto>('/provider-collections/providers', {
        organizationId,
        connectionId,
      }),
    enabled: Boolean(organizationId),
  });
}

export function useProviderCollectionsSummary(
  filter: {
    organizationId?: string;
    provider?: string;
    date?: string;
    targetConnectionId?: string;
  },
  connectionId?: string,
) {
  const enabled = Boolean(filter.organizationId && filter.provider && filter.date);
  return useQuery({
    queryKey: queryKeys.providerCollectionsSummary(connectionId, filter),
    queryFn: () =>
      api.get<ProviderCollectionsSummaryDto>('/provider-collections/summary', {
        organizationId: filter.organizationId,
        provider: filter.provider,
        date: filter.date,
        targetConnectionId: filter.targetConnectionId,
        connectionId,
      }),
    enabled,
  });
}

export function useProviderCollectionsSample(
  filter: {
    organizationId?: string;
    provider?: string;
    date?: string;
    schema?: string;
    table?: string;
    targetConnectionId?: string;
    limit?: number;
  },
  connectionId?: string,
) {
  const enabled = Boolean(
    filter.organizationId && filter.provider && filter.date && filter.schema && filter.table,
  );
  return useQuery({
    queryKey: queryKeys.providerCollectionsSample(connectionId, {
      ...filter,
      limit: filter.limit !== undefined ? String(filter.limit) : undefined,
    }),
    queryFn: () =>
      api.get<ProviderCollectionsSampleDto>('/provider-collections/sample', {
        organizationId: filter.organizationId,
        provider: filter.provider,
        date: filter.date,
        schema: filter.schema,
        table: filter.table,
        targetConnectionId: filter.targetConnectionId,
        limit: filter.limit,
        connectionId,
      }),
    enabled,
  });
}

// ─── Lookups no banco alvo ──────────────────────────────────────────────────

const TARGET_QUERY = { refetchOnMount: 'always' as const };

export function useTargetOrganizations(search: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.organizations(search),
    queryFn: () => api.get<TargetOrganizationDto[]>('/target/organizations', { search }),
    enabled,
    ...TARGET_QUERY,
  });
}

export function useTargetBrands(organizationId?: string) {
  return useQuery({
    queryKey: queryKeys.brands(organizationId ?? ''),
    queryFn: () => api.get<TargetBrandDto[]>('/target/brands', { organizationId }),
    enabled: Boolean(organizationId),
    ...TARGET_QUERY,
  });
}

export function useTargetCategories(organizationId?: string) {
  return useQuery({
    queryKey: queryKeys.categories(organizationId ?? ''),
    queryFn: () => api.get<TargetCategoryDto[]>('/target/categories', { organizationId }),
    enabled: Boolean(organizationId),
    ...TARGET_QUERY,
  });
}

export function useTargetBusinessUnits(organizationId?: string) {
  return useQuery({
    queryKey: queryKeys.businessUnits(organizationId ?? ''),
    queryFn: () => api.get<TargetBusinessUnitDto[]>('/target/business-units', { organizationId }),
    enabled: Boolean(organizationId),
    ...TARGET_QUERY,
  });
}

export function useTargetRetailers(organizationId?: string) {
  return useQuery({
    queryKey: queryKeys.retailers(organizationId ?? ''),
    queryFn: () => api.get<TargetRetailerDto[]>('/target/retailers', { organizationId }),
    enabled: Boolean(organizationId),
    ...TARGET_QUERY,
  });
}

export function useTargetUsers(search: string) {
  return useQuery({
    queryKey: queryKeys.users(search),
    queryFn: () => api.get<TargetUserDto[]>('/target/users', { search }),
    ...TARGET_QUERY,
  });
}
